import { z } from "zod";
import {
  TEXT_AGGREGATES,
  compareText,
  fail,
  ok,
  textOperators,
  type FieldKind,
} from "../kind";
import { scalarToText } from "./text";

/**
 * Un lien : l'adresse, et ce qu'on sait de la page quand on l'a récupéré
 * (comme un LinkNode). Seule l'adresse s'écrit à la main ; le titre, l'image
 * et la description sont ajoutés par l'interface après coup.
 */
export type UrlValue = {
  href: string;
  title?: string;
  image?: string;
  description?: string;
};

export const urlSettings = z.object({
  // url : l'adresse brute · domain : « github.com » · title : le titre de la
  // page (à défaut, le domaine).
  display: z.enum(["url", "domain", "title"]).default("title"),
  openIn: z.enum(["tab", "window"]).default("tab"),
});

const MAX_URL_LENGTH = 2048;

/** « exemple.fr/page » → « https://exemple.fr/page » ; `null` si ce n'en est pas une. */
export function normalizeUrl(input: string): string | null {
  const text = input.trim();
  if (text === "" || /\s/.test(text)) return null;
  const withScheme = /^[a-z][a-z\d+.-]*:/i.test(text) ? text : `https://${text}`;
  let url: URL;
  try {
    url = new URL(withScheme);
  } catch {
    return null;
  }
  if (url.protocol !== "http:" && url.protocol !== "https:") return null;
  // Un hôte sans point (« bonjour ») n'est pas une adresse web, sauf localhost.
  if (!url.hostname.includes(".") && url.hostname !== "localhost") return null;
  return url.toString();
}

export function urlDomain(href: string): string {
  try {
    return new URL(href).hostname.replace(/^www\./, "");
  } catch {
    return href;
  }
}

/** Le texte d'un lien tel qu'on le lit : son titre, sinon son domaine. */
export function urlLabel(value: UrlValue): string {
  return value.title?.trim() || urlDomain(value.href);
}

const metaText = (max: number) => z.string().trim().min(1).max(max).optional();

const urlValueSchema = z.object({
  href: z.string().min(1).max(MAX_URL_LENGTH),
  title: metaText(500),
  image: z.string().max(MAX_URL_LENGTH).optional(),
  description: metaText(2000),
});

function optionalText(value: unknown, max: number): string | undefined {
  return typeof value === "string" && value.trim() !== "" ? value.trim().slice(0, max) : undefined;
}

export const urlKind: FieldKind<"url"> = {
  type: "url",
  settings: urlSettings,
  value: urlValueSchema,
  coerce(input) {
    // Un lien complet (`{ href, title… }`, ou `{ url }`) garde ses métadonnées.
    let raw: string | null;
    let meta: Omit<UrlValue, "href"> = {};
    if (input && typeof input === "object" && !Array.isArray(input)) {
      const object = input as Record<string, unknown>;
      raw = scalarToText(object.href ?? object.url ?? null);
      meta = {
        title: optionalText(object.title, 500),
        image: optionalText(object.image, MAX_URL_LENGTH),
        description: optionalText(object.description, 2000),
      };
    } else {
      raw = scalarToText(input);
    }
    if (raw === null) return fail("Expected a URL.");
    if (raw.trim() === "") return ok(null);
    const href = normalizeUrl(raw);
    if (!href) return fail(`"${raw}" is not a web address.`);
    if (href.length > MAX_URL_LENGTH) return fail("URL is too long.");
    const value: UrlValue = { href };
    if (meta.title) value.title = meta.title;
    if (meta.image) value.image = meta.image;
    if (meta.description) value.description = meta.description;
    return ok(value);
  },
  // En `plain`, l'adresse seule : relisible, et c'est ce qu'on recopie.
  format: (value, settings, ctx) => {
    if (ctx.style === "plain" || settings.display === "url") return value.href;
    return settings.display === "domain" ? urlDomain(value.href) : urlLabel(value);
  },
  compare: (a, b) => compareText(urlLabel(a), urlLabel(b)) || compareText(a.href, b.href),
  // On cherche dans le titre comme dans l'adresse.
  filters: textOperators<"url">((value) => `${value.title ?? ""} ${value.href}`),
  aggregates: TEXT_AGGREGATES,
};
